import { useState } from 'react'
import { X, Loader2, Link } from 'lucide-react'
import type { Competitor, JinaScrapeResult } from '@ispot/shared'
import { useAIStore } from '@/stores/aiStore'
import { competitorMapApi } from '@/lib/api/client'

interface AddCompetitorModalProps {
  accountId: string
  projectId: string
  onAdd: (data: Partial<Competitor>) => Promise<void>
  onClose: () => void
  existingCategories: string[]
}

// JinaScrapeResult comes from @ispot/shared, but we also handle legacy format

export function AddCompetitorModal({ accountId, projectId, onAdd, onClose, existingCategories }: AddCompetitorModalProps) {
  const apiKey = useAIStore(state => state.apiKey)
  const [form, setForm] = useState<Partial<Competitor>>({
    type: 'direct',
    tier: 'established',
    isAiFirst: false,
  })
  const [saving, setSaving] = useState(false)
  const [scraping, setScraping] = useState(false)
  const [scrapeError, setScrapeError] = useState('')
  const [scrapeInfo, setScrapeInfo] = useState<{ pagesFetched?: string; confidence?: string }>({})
  const [lowConfidenceFields, setLowConfidenceFields] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name?.trim()) return
    setSaving(true)
    setError('')
    try {
      await onAdd(form)
      onClose()
    } catch {
      setError('Failed to add competitor')
      setSaving(false)
    }
  }

  async function handleFetchFromUrl() {
    const url = form.url?.trim()
    if (!url) return
    if (!apiKey) {
      setScrapeError('No API key configured — set it in AI Settings first.')
      return
    }

    setScraping(true)
    setScrapeError('')
    setScrapeInfo({})
    try {
      const result = await competitorMapApi.scrapeUrl(accountId, projectId, { url, apiKey }) as
        | JinaScrapeResult
        | { error: string }

      if ('error' in result) {
        setScrapeError(result.error)
        return
      }

      setScrapeInfo({ pagesFetched: result.pagesFetched, confidence: result.confidence })

      // Map JinaScrapeResult to form fields (never overwrite non-empty values)
      setForm(f => {
        const updated = { ...f }
        if (!updated.name && result.name) updated.name = result.name
        if (!updated.whatTheyDo && (result.positioning || result.headline)) {
          updated.whatTheyDo = result.positioning || result.headline || ''
        }
        if (!updated.strengths && result.keyCapabilities?.length) {
          updated.strengths = result.keyCapabilities.join(', ')
        }
        if (!updated.gaps && result.notableGaps?.length) {
          updated.gaps = result.notableGaps.join(', ')
        }
        if (!updated.hq && result.hqLocation) updated.hq = result.hqLocation
        if (!updated.logoUrl && result.logoUrl) updated.logoUrl = result.logoUrl
        return updated
      })

      setLowConfidenceFields(result.confidence === 'low' ? new Set(['name', 'whatTheyDo', 'strengths', 'gaps', 'hq']) : new Set())
    } catch {
      setScrapeError('Failed to fetch URL')
    } finally {
      setScraping(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    fontSize: '13px',
    background: 'white',
    color: 'var(--color-text)',
    boxSizing: 'border-box',
  }

  const labelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    marginBottom: '4px',
    display: 'block',
  }

  const fieldStyle: React.CSSProperties = { marginBottom: '14px' }

  function fieldLabel(key: string, text: string) {
    return (
      <label style={labelStyle}>
        {text}
        {lowConfidenceFields.has(key) && (
          <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}> (uncertain)</span>
        )}
      </label>
    )
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{ background: 'white', borderRadius: '14px', width: '480px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(29,26,21,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--color-text)', fontFamily: 'var(--font-display)' }}>Add Competitor</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '4px' }}><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Name *</label>
              <input
                style={inputStyle}
                placeholder="e.g. Productboard"
                value={form.name ?? ''}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                autoFocus
                required
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Category</label>
              <input
                list="categories"
                style={inputStyle}
                placeholder="e.g. Product Analytics"
                value={form.category ?? ''}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              />
              <datalist id="categories">
                {existingCategories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={form.type ?? 'direct'} onChange={e => setForm(f => ({ ...f, type: e.target.value as Competitor['type'] }))}>
                <option value="direct">Direct</option>
                <option value="adjacent">Adjacent</option>
                <option value="potential">Potential</option>
              </select>
            </div>

            <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>URL</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="https://"
                  value={form.url ?? ''}
                  onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={handleFetchFromUrl}
                  disabled={scraping || !form.url?.trim() || !apiKey}
                  title={!apiKey ? 'Configure API key in AI Settings first' : 'Auto-populate fields from URL'}
                  style={{
                    padding: '8px 12px',
                    background: 'var(--color-warm-100)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                    cursor: scraping || !form.url?.trim() || !apiKey ? 'not-allowed' : 'pointer',
                    opacity: !form.url?.trim() || !apiKey ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    flexShrink: 0,
                    fontWeight: 500,
                  }}
                >
                  {scraping ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Link size={13} />}
                  {scraping ? 'Fetching…' : 'Fetch'}
                </button>
              </div>
              {scrapeError && (
                <p style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>{scrapeError}</p>
              )}
              {scrapeInfo.pagesFetched && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>{scrapeInfo.pagesFetched} pages</span>
                  {scrapeInfo.confidence && (
                    <span style={{
                      fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: 600,
                      ...(scrapeInfo.confidence === 'high' ? { background: 'rgba(232,67,0,0.15)', color: 'var(--color-accent)' }
                        : scrapeInfo.confidence === 'med' ? { background: 'rgba(253,126,20,0.15)', color: '#fd7e14' }
                        : { background: 'rgba(108,117,125,0.15)', color: 'var(--color-text-muted)' }),
                    }}>
                      {scrapeInfo.confidence} confidence
                    </span>
                  )}
                </div>
              )}
            </div>

            <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
              {fieldLabel('whatTheyDo', 'What They Do')}
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }}
                placeholder="Brief description of their product/service…"
                value={form.whatTheyDo ?? ''}
                onChange={e => setForm(f => ({ ...f, whatTheyDo: e.target.value }))}
              />
            </div>

            <div style={fieldStyle}>
              {fieldLabel('strengths', 'Strengths')}
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} value={form.strengths ?? ''} onChange={e => setForm(f => ({ ...f, strengths: e.target.value }))} />
            </div>
            <div style={fieldStyle}>
              {fieldLabel('gaps', 'Gaps')}
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} value={form.gaps ?? ''} onChange={e => setForm(f => ({ ...f, gaps: e.target.value }))} />
            </div>

            <div style={{ ...fieldStyle, gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" id="isAiFirst" checked={!!form.isAiFirst} onChange={e => setForm(f => ({ ...f, isAiFirst: e.target.checked, tier: e.target.checked ? 'ai-first' : 'established' }))} style={{ width: '16px', height: '16px', accentColor: 'var(--color-accent)' }} />
              <label htmlFor="isAiFirst" style={{ fontSize: '13px', color: 'var(--color-text)', cursor: 'pointer' }}>AI-First product</label>
            </div>
          </div>

          {error && <p style={{ color: '#dc3545', fontSize: '12px', marginBottom: '12px' }}>{error}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', background: 'none', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving || !form.name?.trim()} style={{ padding: '8px 18px', background: 'var(--color-accent)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: saving || !form.name?.trim() ? 0.6 : 1 }}>
              {saving ? 'Adding…' : 'Add Competitor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
